import {
  Toaster as Sonner,
  toast as sonnerToast,
  type ExternalToast,
} from "sonner";

import { useSelector } from "react-redux";
import { selectTheme } from "@/app/store/slices/ui.slice";

type ToasterProps = React.ComponentProps<typeof Sonner>;

type Position =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top-center"
  | "bottom-center";

interface CustomToasterProps extends Omit<ToasterProps, "position"> {
  position?: Position;
}

// Single Toaster for a specific position
const SingleToaster = ({
  position = "top-right",
  ...props
}: CustomToasterProps) => {
  const theme = useSelector(selectTheme);

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position={position}
      className="toaster group"
      toastOptions={{
        unstyled: true,
        classNames: {
          toast:
            "group toast bg-white rounded-xl shadow-md border border-grey-200 px-3 py-5 min-w-[350px]",
          description: "text-grey-500",
          actionButton: "bg-grey-900 text-white",
          cancelButton: "bg-grey-100 text-grey-900",
        },
      }}
      {...props}
    />
  );
};

const Toaster = () => {
  return (
    <>
      <SingleToaster position="top-right" />
    </>
  );
};

// Toast options with position support
interface ToastOptions extends ExternalToast {
  position?: Position;
}

type ToastMessage = string | React.ReactNode;

const toast = (message: ToastMessage, options?: ToastOptions) => {
  const { position = "top-right", ...rest } = options || {};
  return sonnerToast(message, { ...rest, position });
};

// Custom toast with position support
toast.custom = (
  jsx: (id: number | string) => React.ReactElement,
  options?: ToastOptions
) => {
  const { position = "top-right", ...rest } = options || {};
  return sonnerToast.custom(jsx, { ...rest, position });
};

toast.promise = sonnerToast.promise;
toast.dismiss = sonnerToast.dismiss;

export { Toaster, SingleToaster, toast };
export type { Position, ToastOptions };
